import { useState, useEffect, useCallback } from 'react';
import { getJobPostings } from '@/services/job.service';
import { candidateService } from '@/services/candidate.service';
import { interviewService } from '@/services/interview.service';
import { useAuthStore } from '@/stores/authStore';

export interface HiringStats {
  openPositions: number;
  activeCandidates: number;
  interviewsThisWeek: number;
  totalJobs: number;
  totalCandidates: number;
  totalInterviews: number;
  newCandidates: number;
  hiredCandidates: number;
  rejectedCandidates: number;
  interviewsScheduled: number;
  interviewsCompleted: number;
}

export interface HiringData {
  stats: HiringStats;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export const useHiringData = (): HiringData => {
  const [stats, setStats] = useState<HiringStats>({
    openPositions: 0,
    activeCandidates: 0,
    interviewsThisWeek: 0,
    totalJobs: 0,
    totalCandidates: 0,
    totalInterviews: 0,
    newCandidates: 0,
    hiredCandidates: 0,
    rejectedCandidates: 0,
    interviewsScheduled: 0,
    interviewsCompleted: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const { hasPermission } = useAuthStore();
  const fetchHiringData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const canViewJobs = hasPermission('JOB_VIEW');
      const canViewCandidates = hasPermission('CANDIDATE_VIEW');

      // Fetch all data in parallel
      const [jobsResponse, candidatesResponse, interviewsResponse] = await Promise.all([
        canViewJobs ? getJobPostings().catch(err => {
          console.error('Jobs API error:', err);
          return { data: [] };
        }) : Promise.resolve({ data: [] }),
        canViewCandidates ? candidateService.getCandidates({ limit: 1000 }).catch(err => {
          console.error('Candidates API error:', err);
          return { data: [] };
        }) : Promise.resolve({ data: [] }),
        canViewCandidates ? interviewService.getInterviews({}).catch(err => {
          console.error('Interviews API error:', err);
          return { data: [] };
        }) : Promise.resolve({ data: [] }),
      ]);

      // Process jobs data
      let jobsData: any[] = [];
      if (jobsResponse && typeof jobsResponse === 'object') {
        if (Array.isArray(jobsResponse.data)) {
          jobsData = jobsResponse.data;
        } else if (Array.isArray(jobsResponse)) {
          jobsData = jobsResponse;
        }
      }

      // Filter active/open jobs
      const activeJobs = jobsData.filter((job: any) => {
        if (!job || job.isActive === false) return false;

        const jobStatus = typeof job.status === 'string'
          ? job.status.toLowerCase()
          : job.status?.name?.toLowerCase() || '';

        return jobStatus === 'open' || jobStatus === 'active';
      });

      // Process candidates data
      let candidatesData: any[] = [];
      if (candidatesResponse && typeof candidatesResponse === 'object') {
        if (Array.isArray(candidatesResponse.data)) {
          candidatesData = candidatesResponse.data;
        } else if (Array.isArray(candidatesResponse)) {
          candidatesData = candidatesResponse;
        }
      }

      // Filter active candidates (not rejected or withdrawn)
      const activeCandidates = candidatesData.filter((candidate: any) => {
        if (!candidate || candidate.isActive === false || candidate.isDeleted === true) return false;

        const candidateStatus = typeof candidate.status === 'string'
          ? candidate.status.toLowerCase()
          : candidate.status?.name?.toLowerCase() || '';

        return candidateStatus !== 'rejected' && candidateStatus !== 'withdrawn';
      });

      // Count candidates by status
      const getCandidateStatus = (candidate: any) => {
        if (typeof candidate.status === 'string') {
          return candidate.status.toLowerCase();
        } else if (candidate.status && typeof candidate.status === 'object') {
          return (candidate.status.name || candidate.status.status || 'unknown').toLowerCase();
        }
        return 'unknown';
      };

      const newCandidates = activeCandidates.filter((c: any) => {
        const status = getCandidateStatus(c);
        return status === 'new' || status === 'applied' || status === 'pending';
      }).length;

      const hiredCandidates = activeCandidates.filter((c: any) => {
        const status = getCandidateStatus(c);
        return status === 'hired' || status === 'selected' || status === 'accepted';
      }).length;

      const rejectedCandidates = candidatesData.filter((c: any) => {
        const status = getCandidateStatus(c);
        return status === 'rejected' || status === 'declined' || status === 'not_selected';
      }).length;

      // Process interviews data
      let interviewsData: any[] = [];
      if (interviewsResponse && typeof interviewsResponse === 'object') {
        if (Array.isArray(interviewsResponse.data)) {
          interviewsData = interviewsResponse.data;
        } else if (Array.isArray(interviewsResponse)) {
          interviewsData = interviewsResponse;
        }
      }

      // Calculate interviews this week
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const interviewsThisWeek = interviewsData.filter((interview: any) => {
        if (!interview.interviewDate) return false;
        const interviewDate = new Date(interview.interviewDate);
        return interviewDate >= startOfWeek && interviewDate <= now;
      }).length;

      // Count interviews by status
      const getInterviewStatus = (interview: any) => {
        if (typeof interview.status === 'string') {
          return interview.status.toLowerCase();
        } else if (interview.status && typeof interview.status === 'object') {
          return (interview.status.name || interview.status.status || 'unknown').toLowerCase();
        }
        return 'unknown';
      };

      const interviewsScheduled = interviewsData.filter((interview: any) => {
        const status = getInterviewStatus(interview);
        return status === 'scheduled' || status === 'pending';
      }).length;

      const interviewsCompleted = interviewsData.filter((interview: any) => {
        const status = getInterviewStatus(interview);
        return status === 'completed' || status === 'finished';
      }).length;

      // Update stats
      const newStats: HiringStats = {
        openPositions: activeJobs.length,
        activeCandidates: activeCandidates.length,
        interviewsThisWeek,
        totalJobs: jobsData.length,
        totalCandidates: candidatesData.length,
        totalInterviews: interviewsData.length,
        newCandidates,
        hiredCandidates,
        rejectedCandidates,
        interviewsScheduled,
        interviewsCompleted,
      };

      setStats(newStats);
      setLastUpdated(new Date());

    } catch (err) {
      console.error('Error fetching hiring data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch hiring data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHiringData();
  }, [fetchHiringData]);

  return {
    stats,
    loading,
    error,
    lastUpdated,
  };
}; 